import React, { useCallback } from 'react';
import {
  Divider,
  Navbar,
  QredLogo,
  useModals,
  useWindowProperties,
} from '@qred/components-library';
import { useHistory, useLocation } from 'react-router';
import useLogout from '~/hooks/useLogout';
import {
  openBecomeTesterPage,
  openFactoringPage,
  openQredCardPage,
} from '~/services/links';
import useTranslate from '~/hooks/useTranslate';
import { SwitchCompanyButton } from './SwitchCompanyButton';
import { LanguageItemSideBar } from './LanguageItemSideBar';
import { useClient } from '~/hooks/useClient';
import { CountryCode } from '~/enums';
import constVars from '~/constants/constVars';
import ContactUsModal from '~/components/shared/Navigation/ContactUsModal';
import {
  PostHogPageName,
  capturePostHogEvent,
} from '~/store/actions/posthogActions';
import { useDispatch } from 'react-redux';
import { posthogEventNames } from '~/pages/Dashboard/constants';

const SideBar = () => {
  const { language, market, companies } = useClient();
  const { isMobile, isTablet } = useWindowProperties();
  const tabletSpacing = isTablet ? 25 : 28;

  const showQredCard = market === CountryCode.SE || market === CountryCode.FI;
  const showFactoring = market === CountryCode.SE;
  const isQeld = market === CountryCode.NL;

  /* TODO: This code will be replaced once the invoices page is ready */

  const development =
    import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test';

  const t = useTranslate();
  const logout = useLogout();
  const { pathname } = useLocation();
  const history = useHistory();
  const modals = useModals();
  const dispatch = useDispatch();

  const handleLogout = () => {
    logout();
    dispatch(
      capturePostHogEvent({
        product: PostHogPageName.DASHBOARD,
        eventName: posthogEventNames.LOG_OUT,
      })
    );
  };

  const onOpenPage = useCallback(
    (name) => {
      switch (name) {
        case 'home':
          history.push('/');
          break;
        case 'qred-visa':
          openQredCardPage(market, language);
          dispatch(
            capturePostHogEvent({
              product: PostHogPageName.DASHBOARD,
              eventName: posthogEventNames.GET_QRED_VISA,
            })
          );
          break;
        case 'sell-your-invoices':
          openFactoringPage(language);
          dispatch(
            capturePostHogEvent({
              product: PostHogPageName.DASHBOARD,
              eventName: posthogEventNames.SELL_YOUR_INVOICES,
            })
          );
          break;
        case 'become-a-tester':
          openBecomeTesterPage(market, language);
          dispatch(
            capturePostHogEvent({
              product: PostHogPageName.DASHBOARD,
              eventName: posthogEventNames.BECOME_A_TESTER,
            })
          );
          break;
        default:
          break;
      }
    },
    [market, language]
  );

  const handleOpenContactUsModal = () => {
    modals.openModal({
      title: t('Onboarding.ContactUs'),
      children: <ContactUsModal />,
    });
  };

  const handleOnClickBecomeTester = () => {
    onOpenPage('become-a-tester');
    dispatch(
      capturePostHogEvent({
        product: PostHogPageName.DASHBOARD,
        eventName: posthogEventNames.BECOME_A_TESTER,
      })
    );
  };

  const handleOnClickQredVisa = () => {
    onOpenPage('qred-visa');
    dispatch(
      capturePostHogEvent({
        product: PostHogPageName.DASHBOARD,
        eventName: posthogEventNames.GET_QRED_VISA,
      })
    );
  };
  const handleOnClickFactoring = () => {
    onOpenPage('sell-your-invoices');
    dispatch(
      capturePostHogEvent({
        product: PostHogPageName.DASHBOARD,
        eventName: posthogEventNames.SELL_YOUR_INVOICES,
      })
    );
  };
  return (
    <Navbar
      width={247}
      collapseTooltipLabels={{
        open: `${t('Dashboard.OpenSidebar')}`,
        close: `${t('Dashboard.CloseSidebar')}`,
      }}
    >
      <Navbar.Section pl={32} pt={tabletSpacing} pb={40}>
        <QredLogo
          width={81.12}
          qeld={isQeld}
          onClick={() => history.push('/')}
        />
      </Navbar.Section>
      <Navbar.Section mb={!development ? 20 : 0}>
        <Navbar.Link
          active={pathname === `/`}
          onClick={() => history.push(`/`)}
        >
          {t('Home')}
        </Navbar.Link>
        {isMobile && (
          <Navbar.Link
            active={pathname === `/${constVars.ROUTE_PROFILE}`}
            onClick={() => history.push(`/${constVars.ROUTE_PROFILE}`)}
          >
            {t('Profile')}
          </Navbar.Link>
        )}
      </Navbar.Section>
      {development && (
        <Navbar.Section mb={20}>
          <Navbar.Link
            active={pathname === `/${constVars.ROUTE_INVOICES}`}
            onClick={() => history.push(`/${constVars.ROUTE_INVOICES}`)}
          >
            {t('Dashboard.Invoices')}
          </Navbar.Link>
        </Navbar.Section>
      )}
      <Navbar.Section grow>
        <Navbar.Title>{t('Dashboard.Explore')}</Navbar.Title>
        {showQredCard && (
          <Navbar.Link onClick={handleOnClickQredVisa} rightIcon="open_in_new">
            {t('QredCard')}
          </Navbar.Link>
        )}
        {showFactoring && (
          <Navbar.Link rightIcon="open_in_new" onClick={handleOnClickFactoring}>
            {t('Dashboard.FactoringSentence1')}
          </Navbar.Link>
        )}
        <Navbar.Link
          onClick={handleOnClickBecomeTester}
          rightIcon="open_in_new"
        >
          {t('Dashboard.BecomeATester')}
        </Navbar.Link>
      </Navbar.Section>
      <Divider color="neutral.16" my="1.5em" />
      <Navbar.Section pb="1.7em">
        {isMobile && !isQeld && <LanguageItemSideBar />}
        <Navbar.Link rightIcon="help" onClick={handleOpenContactUsModal}>
          {t('Onboarding.ContactUs')}
        </Navbar.Link>
        {companies && companies.length > 1 && <SwitchCompanyButton />}
        <Navbar.Link rightIcon="logout" onClick={handleLogout}>
          {t('Logout')}
        </Navbar.Link>
      </Navbar.Section>
    </Navbar>
  );
};

export default SideBar;
